import classnames from 'classnames';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

// import { FaTrash } from "react-icons/fa"
// import { EditIcon } from "src/ui/common/icons"

import { makePaginated } from '../../../../../adl-gen/common';
import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import {
  ApplicationRole,
  AppUser,
  AppUserStatus,
  makeAppUser
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { AppUserListEntry } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { UserData } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { SelectOption } from '../../../../../ui/widgets/select/select';
import { newEmailFieldState, newTextField } from '../../../../../ui/widgets/text-input/text-field';
import { uniqueId } from '../../../../../util/utils';
import { AppUserStore, UserGroupChange, UserQuizChange } from '../../../../stores';
import {
  CheckItemObserver,
  deleteHandler,
  renderButtons,
  SelectObserver,
  TextInputObserver
} from '../../../common/editor';
import { AdminApplicationRoleType, ApplicationRoleType } from '../../../common/list/list-types';
import { ListView } from '../../../common/list/list-view';
import { AppUserListEntryWithQuizChanges, AppUserQuizListView, QuizListEntry } from '../../lists/quiz-list/quiz-list-view';
import { UserQuizListEntry, UserQuizListView } from '../../lists/quiz-list/user-quiz-list-view';
import {
  AppUserGroupListEntry,
  AppUserGroupListView,
  AppUserGroups
} from '../../lists/usergroup-list/usergroup-list-view';
// import { DangerZone } from "../danger-zone/danger-zone"

const styles = require('../../../common/editor.css');
// const buttonStyles = require("../../common/common.css")

interface AppUserEditorProps {
  debug?: boolean;
  item?: AppUserListEntry;
  data: UserData;
  store: AppUserStore;
  onCreate(
    item: AppUser,
    addToGlobalGroup: boolean,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[]
  ): Promise<void>;
  onUpdate?(
    item: WithDbId<AppUser>,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[],
    reactivating?: boolean
  ): Promise<void>;
  onCancel(): void;
  applicationRole: AdminApplicationRoleType;
}

/**
 * Component to maintain Users.
 * Extra DML to manage AppUserSeats
 */

@observer
export class AppUserEditor extends React.Component<AppUserEditorProps> {
  @observable fieldState = {
    fullname: newTextField(),
    email: newEmailFieldState(),

    // FF2-463: New role handling to include Client Admin
    applicationRole: ApplicationRole.user,

    // Default status active
    // TODO: Make this a full AppUserStatus object when 'deleted' is supported (soft-deletes)
    status: true,

    // Use with New User only (not updates)
    createWithGlobalGroup: true,

    // '' = no manager
    managerAppUserId: '' as string,

    quizzes: Array<UserQuizChange>(),

    assignedGroups: Array<UserGroupChange>()
  };

  @observable locked = false;

  // TODO: replace switcheroo with actual delete from ListView observable
  @observable groupsTabKey: React.ReactText = 0;
  @observable assessmentsTabKey: React.ReactText = 0;

  constructor(props: AppUserEditorProps) {
    super(props);
    const state = this.fieldState;
    if (props.item) {
      state.fullname.onChange(props.item.fullname);
      /// If this user already exists the email address is read-only - but we still want to display it (in a disabled field)
      state.email.onChange(props.item.email);
      state.applicationRole = props.item.role; // === ApplicationRole.super ? "super" : value.role === ApplicationRole.admin ? "admin" : "user"
      state.status = props.item.status === AppUserStatus.active ? true : false;
      state.managerAppUserId = props.item.managerId || '';
      state.quizzes = props.item.quizzes.map(uq => ({
        kind: 'loaded',
        value: uq
      }));
      // Groups: Set up managers first
      for (const managerOf of props.item.managerOf) {
        const group = props.data.groupList.find(g => g.id === managerOf.value.userGroupId);
        if (group) {
          state.assignedGroups.push({
            kind: 'loaded',
            userId: managerOf.value.appUserId,
            managerOf: managerOf.id,
            groupId: group.id
          });
        }
      }
      // Then update/insert for members
      for (const member of props.item.memberOf) {
        const group = props.data.groupList.find(g => g.id === member.value.userGroupId);
        if (group) {
          const existingIdx = state.assignedGroups.findIndex(ag => ag.groupId === group.id);
          if (existingIdx >= 0) {
            const existing = state.assignedGroups[existingIdx];
            existing.memberOf = member.id;
            state.assignedGroups.splice(existingIdx, 1, existing);
          } else {
            state.assignedGroups.push({
              kind: 'loaded',
              userId: member.value.appUserId,
              memberOf: member.id,
              groupId: group.id
            });
          }
        }
      }
    } else {
      state.email.onChange('');
    }
  }

  @action
  onChangeGroupsTab = (nextKey: React.ReactText) => {
    this.groupsTabKey = nextKey;
  };

  @action
  onChangeAssessmentsTab = (nextKey: React.ReactText) => {
    this.assessmentsTabKey = nextKey;
  };

  @action lockUpdates = () => (this.locked = true);

  @action unlockUpdates = () => (this.locked = false);

  @action
  onSetApplicationRole = (role: SelectOption) => {
    this.fieldState.applicationRole = this.stringToRole(role.value as ApplicationRoleType);
  };

  stringToRole = (role: ApplicationRoleType): ApplicationRole => {
    switch (role) {
      case 'super':
        return ApplicationRole.super;
      case 'client-admin':
        return ApplicationRole.admin;
      default:
        return ApplicationRole.user;
    }
  };

  roleToString = (role: ApplicationRole): ApplicationRoleType => {
    switch (role) {
      case ApplicationRole.super:
        return 'super';
      case ApplicationRole.admin:
        return 'client-admin';
      default:
        return 'user';
    }
  };

  selectedOption = (role: ApplicationRole): SelectOption => {
    switch (role) {
      case ApplicationRole.super:
        return {
          label: 'super',
          value: this.roleToString(ApplicationRole.super)
        };
      case ApplicationRole.admin:
        return {
          label: 'admin',
          value: this.roleToString(ApplicationRole.admin)
        };
      default:
        return {
          label: 'user',
          value: this.roleToString(ApplicationRole.user)
        };
    }
  };

  roleOptions = (): SelectOption[] => {
    return [
      /// TODO: Super needs to be able to create super users
      // this.selectedOption(ApplicationRole.super),
      this.selectedOption(ApplicationRole.admin),
      this.selectedOption(ApplicationRole.user)
    ];
  };

  @action
  onSetManager = (option?: SelectOption) => {
    this.fieldState.managerAppUserId = option ? (option.value as string) : '';
  };

  managerOptions = (): SelectOption[] => {
    const { item, store } = this.props;
    const loadingList = store.loadingList;
    const users = loadingList.kind === 'value' ? loadingList.value.items : [];
    const options = users
      .filter(u => !item || u.id !== item.id)
      .map(u => ({ label: `${u.fullname} (${u.email})`, value: u.id }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase(), 'en', { sensitivity: 'base' }));
    return [{ label: 'None', value: '' }, ...options];
  };

  selectedManagerOption = (): SelectOption => {
    const options = this.managerOptions();
    return options.find(o => o.value === this.fieldState.managerAppUserId) || options[0];
  };

  ManagerObserver = observer(() =>
    SelectObserver({
      options: this.managerOptions(),
      selectedOption: this.selectedManagerOption(),
      title: "User's Manager",
      xsSpan: 3,
      onChange: this.onSetManager,
      debug: !!this.props.debug
    })
  );

  FullNameObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.fullname,
      type: 'text',
      title: 'User Name',
      placeholder: '',
      xsSpan: 2,
      debug: this.props.debug
    })
  );

  EmailObserver = observer(({ disabled }) =>
    TextInputObserver({
      field: this.fieldState.email,
      type: 'email',
      title: 'Email',
      placeholder: 'Valid Email',
      xsSpan: 1,
      debug: this.props.debug,
      disabled
    })
  );

  ApplicationRoleObserver = observer(() =>
    SelectObserver({
      options: this.roleOptions(),
      selectedOption: this.selectedOption(this.fieldState.applicationRole),
      title: 'User Type',
      xsSpan: 1,
      onChange: this.onSetApplicationRole,
      debug: !!this.props.debug
    })
  );

  /// TODO: Enable 'deleted' later when we're ready to support soft-deletes
  StatusObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.status,
      label: 'Active/Suspended (clear to Suspend user)',
      xsSpan: 2,
      onChange: this.toggleActive,
      debug: this.props.debug
    })
  );

  CreateWithGlobalGroupObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.createWithGlobalGroup,
      label: 'Add User to Global group',
      xsSpan: 2,
      onChange: this.toggleWithGlobalGroup,
      debug: this.props.debug
    })
  );

  // ----- [ TODO: ] -------------------------------------
  // TODO: Disable update of Global group for Client Admin
  // ----- [ TODO: ] -------------------------------------

  AssignedGroupListObserver = observer(() => {
    // Don't display for 'new' - we need a Group ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    return (this.assignedGroupList.length > 0 ?
      <React.Fragment>
        <h5>Assigned Groups</h5>
          <ListView
            // Trigger re-render on change
            key={`${this.assignedGroupList.length}`}
            list={makePaginated({
              items: this.assignedGroupList,
              current_offset: 0,
              total_size: this.assignedGroupList.length
            })}
            fields={AppUserGroupListView}
            refData={this.appUserGroups}
            onToggle={this.onChangeUserGroupAssigment}
          />
      </React.Fragment>
      : null
    );
  });

  UnassignedGroupListObserver = observer(() => {
    return (
      <React.Fragment>
          <h5>Unassigned Groups</h5>
          <ListView
            // Trigger re-render on change
            key={`${this.unassignedGroupList.length}`}
            list={makePaginated({
              items: this.unassignedGroupList,
              current_offset: 0,
              total_size: this.unassignedGroupList.length
            })}
            fields={AppUserGroupListView}
            onToggle={this.onChangeUserGroupAssigment}
          />
      </React.Fragment>
    );
  });

  QuizListObserver = observer(() => {
    const { item } = this.props;
    /// non-updates thing is optional for new users
    const refData: AppUserListEntryWithQuizChanges = item
      ? { ...item, updates: this.fieldState.quizzes }
      : { updates: this.fieldState.quizzes };
    return (
      <React.Fragment>
        <h5>Available Assessments</h5>
          <ListView
            // Watch for changes in userQuizList (quizList doesn't change)
            key={`${this.userQuizList.length}`}
            list={makePaginated({
              items: this.quizList,
              current_offset: 0,
              total_size: this.quizList.length
            })}
            fields={AppUserQuizListView}
            refData={refData}
            onToggle={this.onCreateUserQuiz}
          />
      </React.Fragment>
    );
  });

  UserQuizListObserver = observer(() => {
    return (
      <>
        <h5>Allocated Assessments</h5>
        <ListView
          key={`${this.userQuizList.length}`}
          list={makePaginated({
            items: this.userQuizList,
            current_offset: 0,
            total_size: this.userQuizList.length
          })}
          fields={UserQuizListView}
          refData={this.quizList}
          onDelete={this.onDeleteUserQuiz}
        />
      </>
    );
  });

  @computed
  get inputIsValid(): boolean {
    const valid = this.props.item ? true : this.fieldState.email.getValid() !== undefined;
    /// FIXME: check consistency of groups and assessments
    // if (valid && this.props.item) {
    //   valid = this.props.item.appClientId === this.props.userData.appClientId
    // }
    return valid && this.fieldState.fullname.getValid() !== undefined;
  }

  @computed
  get userQuizChangeList(): UserQuizChange[] {
    return this.fieldState.quizzes.filter(
      uq => uq.kind === 'create' || uq.kind === 'update' || uq.kind === 'delete' || uq.kind === 'purge'
    );
  }

  @computed
  get userGroupChangeList(): UserGroupChange[] {
    const { item: existing } = this.props;
    /// Strategy:
    /// If an item is still 'loaded' - ignore
    /// If it is 'create' - check in case it was loaded for safety - and for each not-undefined item, add create
    /// If it is 'update' - cf against original: defined -> undefined => delete, undefined -> defined => create
    const changes: UserGroupChange[] = this.fieldState.assignedGroups.filter(
      ug => ug.kind === 'create' || ug.kind === 'update'
    );
    const creates: UserGroupChange[] = changes.reduce((acc: UserGroupChange[], next) => {
      if (existing) {
        const existingMember = existing.memberOf.find(m => m.value.userGroupId === next.groupId);
        const existingManager = existing.managerOf.find(m => m.value.userGroupId === next.groupId);
        if (existingMember === undefined || existingManager === undefined) {
          acc.push({
            kind: 'create',
            groupId: next.groupId,
            memberOf: existingMember === undefined ? next.memberOf : undefined,
            managerOf: existingManager === undefined ? next.managerOf : undefined
          });
        }
      } else {
        acc.push({
          kind: 'create',
          groupId: next.groupId,
          memberOf: next.memberOf,
          managerOf: next.managerOf
        });
      }
      return acc;
    }, []);
    const deletes: UserGroupChange[] = changes.reduce((acc: UserGroupChange[], next) => {
      if (existing) {
        const existingMember = existing.memberOf.find(m => m.value.userGroupId === next.groupId);
        const existingManager = existing.managerOf.find(m => m.value.userGroupId === next.groupId);
        if (next.memberOf === undefined || next.managerOf === undefined) {
          acc.push({
            kind: 'delete',
            groupId: next.groupId,
            memberOf: existingMember && next.memberOf === undefined ? existingMember.id : undefined,
            managerOf: existingManager && next.managerOf === undefined ? existingManager.id : undefined
          });
        }
      }
      return acc;
    }, []);
    return [...creates, ...deletes];
  }

  @computed
  get assignedGroupList(): AppUserGroupListEntry[] {
    const { applicationRole, data: userData } = this.props;
    const items = userData.groupList
      .filter(g => {
        const record = this.fieldState.assignedGroups.find(ag => ag.groupId === g.id);
        if (record) {
          return !!record.memberOf || !!record.managerOf;
        }
        return false;
      })
      .map(gWithId => ({
        id: gWithId.id,
        ...gWithId.value,
        identity: applicationRole
      }));
    return items;
  }

  @computed
  get unassignedGroupList(): AppUserGroupListEntry[] {
    const { applicationRole, data: userData } = this.props;
    const items = userData.groupList
      .filter(g => {
        const record = this.fieldState.assignedGroups.find(ag => ag.groupId === g.id);
        if (record) {
          return record.memberOf === undefined && record.managerOf === undefined;
        }
        return true;
      })
      .map(gWithId => ({
        id: gWithId.id,
        ...gWithId.value,
        identity: applicationRole
      }));
    return items;
  }

  @computed
  get appUserGroups(): AppUserGroups {
    return {
      memberOf: this.fieldState.assignedGroups.filter(g => g.memberOf).map(g => g.groupId),
      managerOf: this.fieldState.assignedGroups.filter(g => g.managerOf).map(g => g.groupId)
    };
  }

  @computed
  get quizList(): QuizListEntry[] {
    const { data: userData } = this.props;
    return userData.quizList
        .filter(item => {
          return userData.appClient.value.displayClientAssessmentsOnly
            ? item.value.workRole.owner.kind === 'appClientId' &&
                item.value.workRole.owner.value === userData.appClient.id
            : true;
        })
        .map(qWithId => ({
          id: qWithId.id,
          imageUrl: qWithId.value.imageUrl,
          title: qWithId.value.title.value.en,
          workRoleId: qWithId.value.workRoleId,
          workRole: qWithId.value.workRole,
          lockResults: qWithId.value.lockResults
        }));
  }

  @computed
  get userQuizList(): UserQuizListEntry[] {
    const { data: userData } = this.props;
    const titles = userData.quizList
      .filter(item => {
        return userData.appClient.value.displayClientAssessmentsOnly
          ? item.value.workRole.owner.kind === 'appClientId' &&
              item.value.workRole.owner.value === userData.appClient.id
          : true;
      })
      .map(q => ({ id: q.id, title: q.value.title }));
    const userQuizListEntries = this.fieldState.quizzes //item ?
      .filter(uq => uq.kind === 'loaded' || uq.kind === 'create')
      .reduce((acc: UserQuizListEntry[], uq) => {
        switch (uq.kind) {
          case 'loaded': {
            const { value: userQuiz } = uq.value;
            const q = titles.find(t => t.id === userQuiz.quizId);
            if (q) {
              acc.push({
                id: uq.value.id,
                refId: userQuiz.quizId,
                title: q.title.value.en,
                // title: q ? q.title.value.en : "notfound",
                startedAt: userQuiz.startedAt,
                completedAt: userQuiz.completedAt
              });
            }
            return acc;
          }
          case 'create': {
            const { value: quizId } = uq;
            const q = titles.find(t => t.id === quizId);
            if (q) {
              acc.push({
                id: `CREATED-${uniqueId()}`, /// TODO: uniqueId? Even though it's not a real one
                refId: quizId,
                // title: q ? q.title.value.en : "notfound",
                title: q.title.value.en,
                startedAt: null,
                completedAt: null
              });
            }
            return acc;
          }
          default:
            return acc;
        }
      }, []); //: []

    return userQuizListEntries;
  }

  @action
  toggleActive = () => {
    this.fieldState.status = !this.fieldState.status;
  };

  @action
  toggleWithGlobalGroup = () => {
    this.fieldState.createWithGlobalGroup = !this.fieldState.createWithGlobalGroup;
  };

  @action
  onChangeUserGroupAssigment = (fieldName: string, item: AppUserGroupListEntry, value: boolean): void => {

    const updatedGroups = value ? 
      this.onAddGroupAsMemberOrManager(fieldName, item) :
      this.onRemoveGroupAsMemberOrManager(fieldName, item);
    
    this.fieldState.assignedGroups = updatedGroups;
  }

  onAddGroupAsMemberOrManager = (fieldName: string, item: AppUserGroupListEntry): UserGroupChange[] => {
    const assignedGroups = this.fieldState.assignedGroups.slice();
    // Check if unassigned
    const existingIdx = assignedGroups.findIndex(g => g.groupId === item.id);
    if (existingIdx < 0) {
      // add
      assignedGroups.push({
        kind: 'create',
        groupId: item.id,
        memberOf: fieldName === 'memberOf' ? `${uniqueId()}` : undefined,
        managerOf: fieldName === 'managerOf' ? `${uniqueId()}` : undefined
      });
    } else {
      const existing = assignedGroups[existingIdx];
      const update: UserGroupChange = {
        ...existing,
        kind: existing.kind === 'loaded' ? 'update' : existing.kind,
      };
      if (fieldName === 'memberOf') {
        update.memberOf = existing.memberOf || `${uniqueId()}`;
      };
      if (fieldName === 'managerOf') {
        update.managerOf = existing.managerOf || `${uniqueId()}`;
      }
      // update
      assignedGroups.splice(existingIdx, 1, update);
    }
    return assignedGroups;
  }

  onRemoveGroupAsMemberOrManager = (fieldName: string, item: AppUserGroupListEntry): UserGroupChange[] => {
    const assignedGroups = this.fieldState.assignedGroups.slice();
    // Check if unassigned
    const existingIdx = assignedGroups.findIndex(g => g.groupId === item.id);
    if (existingIdx >= 0) {
      const existing = assignedGroups[existingIdx];
      const update: UserGroupChange = {
        ...existing,
        kind: existing.kind === 'loaded' ? 'update' : existing.kind,
      };
      if (fieldName === 'memberOf') {
        update.memberOf = undefined;
      };
      if (fieldName === 'managerOf') {
        update.managerOf = undefined;
      }
      // update
      assignedGroups.splice(existingIdx, 1, update);
    }
    return assignedGroups;
  }
  @action
  onCreateUserQuiz = (fieldName: string, item: QuizListEntry): void => {
    // Currently only supporting "create"
    if (fieldName === 'create') {
      /// New state not mutated state
      const quizzes = this.fieldState.quizzes.slice();
      /// Inject create into array
      quizzes.push({ kind: 'create', value: item.id });
      this.fieldState.quizzes = quizzes;
    }
  };

  onDeleteUserQuiz = (entry: UserQuizListEntry): Promise<void> => {
    /// If already marked for delete - do nothing, otherwise...
    if (
      this.fieldState.quizzes.find(uq => (uq.kind === 'delete' || uq.kind === 'purge') && uq.value === entry.id) ===
      undefined
    ) {
      /// New state not mutated state
      const quizzes = this.fieldState.quizzes.slice();
      /// Look for individual UserQuiz records first, in case the action has been to delete an existing and then create a new...
      let idx = quizzes.findIndex(uq => (uq.kind === 'loaded' || uq.kind === 'update') && uq.value.id === entry.id);
      if (idx >= 0) {
        quizzes.splice(idx, 1);
        quizzes.push({
          kind: entry.startedAt === null ? 'delete' : 'purge',
          value: entry.id
        });
      } else {
        /// Else if it's a create, just delete it
        idx = quizzes.findIndex(uq => uq.kind === 'create' && uq.value === entry.refId);
        if (idx >= 0) {
          quizzes.splice(idx, 1);
        }
      }
      /// New state not mutated state
      runInAction(() => (this.fieldState.quizzes = quizzes));
    }
    return Promise.resolve();
  };

  onSave = async () => {
    const { item: existingUser, onCreate, onUpdate, data } = this.props;

    runInAction(() => this.lockUpdates());

    /// Build from scratch here to get latest changes
    let user;
    if (existingUser) {
      user = makeAppUser({
        appClientId: data.appClient.id,
        fullname: this.fieldState.fullname.value(),
        email: existingUser.email,
        /// If user = current user (and therefore super (and later admin)), this
        /// should not be updated to a different role. There is a trap in
        /// UserStore to prevent this.
        role: this.fieldState.applicationRole,
        // TODO: implement support for 'deleted' status
        status: this.fieldState.status ? AppUserStatus.active : AppUserStatus.suspended,
        managerId: this.fieldState.managerAppUserId || null
      });
    } else {
      user = makeAppUser({
        appClientId: data.appClient.id,
        fullname: this.fieldState.fullname.value(),
        email: this.fieldState.email.value(),
        role: this.fieldState.applicationRole,
        // TODO: implement support for 'deleted' status
        status: this.fieldState.status ? AppUserStatus.active : AppUserStatus.suspended,
        managerId: this.fieldState.managerAppUserId || null
      });
    }

    // Because of UG members and managers, we need the user ID here
    if (existingUser && onUpdate) {
      const reactivating =
        existingUser.status === AppUserStatus.active && !this.fieldState.status
          ? false
          : existingUser.status === AppUserStatus.suspended && this.fieldState.status
          ? true
          : undefined;
      await onUpdate(
        makeWithDbId({
          id: existingUser.id,
          value: { ...user }
        }),
        this.userGroupChangeList,
        this.userQuizChangeList,
        reactivating
      );
    } else {
      await onCreate(user, this.fieldState.createWithGlobalGroup, this.userGroupChangeList, this.userQuizChangeList);
    }

    runInAction(() => this.unlockUpdates());
  };

  EditEmail = () => {
    let disabled;
    if (this.props.item) {
      disabled = true;
    }
    return <this.EmailObserver disabled={disabled} />;
  };

  Edit = () => (
    // <div className={styles.page}>
    //   <div className={styles.body}>
    <React.Fragment>
      <h4>User Profile</h4>
      <div className={styles.row}>
        <this.FullNameObserver />
        <this.EditEmail />
        <this.ApplicationRoleObserver />
        <this.ManagerObserver />
      </div>
      <div className={classnames(styles.row, styles.checkItemSet)}>
        <this.StatusObserver />
        {this.props.item === undefined ? <this.CreateWithGlobalGroupObserver /> : null}
      </div>
    </React.Fragment>
    //   </div>
    // </div>
  );

  render() {
    return (
      // {/* TODO aria (or DOM) labels */}
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              this.props.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          <this.Edit />
        </div>
        <div className={styles.body}>
          <h4>Groups for {this.props.item ? this.props.item.fullname : 'User'}</h4>
          <div className={styles.row}>
            <this.AssignedGroupListObserver />
          </div>
          <div className={styles.row}>
            <this.UnassignedGroupListObserver />
          </div>
        </div>
        <div className={styles.body}>
          <h4>Assessments for {this.props.item ? this.props.item.fullname : 'User'}</h4>
          <div className={styles.row}>
            <this.UserQuizListObserver />
          </div>
          <div className={styles.row}>
            <this.QuizListObserver />
          </div>
        </div>
        {/* {this.props.item && <div className={styles.body}>
          <DangerZone
              title={'Delete User'}
              content={'Remove user from skillseeker, along with all associated information including results and recommendations. This cannot be undone.'}
              actionText={'Remove User'} 
           /></div>} */}
      </div>
    );
  }
}

export interface UserDeleterProps {
  items?: WithDbId<AppUser>[];
  onDelete?(ids: DbKey<AppUser>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete AppUser */
// @observer
export class UserDeleter extends React.Component<UserDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Users', items.map(item => item.value.fullname))}
          </div>
        </div>
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/appuser-editor/appuser-editor.tsx